import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { supabase } from '../supabaseClient';
import * as pdfjsLib from 'pdfjs-dist';
import 'katex/dist/katex.min.css';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const BlogEditor = ({ blog, onSave, onCancel }) => {
  const [title, setTitle] = useState(blog?.title || '');
  const [slug, setSlug] = useState(blog?.slug || '');
  const [excerpt, setExcerpt] = useState(blog?.excerpt || '');
  const [content, setContent] = useState(blog?.content || '');
  const [coverImage, setCoverImage] = useState(blog?.cover_image || '');
  const [tags, setTags] = useState(blog?.tags?.join(', ') || '');
  const [status, setStatus] = useState(blog?.status || 'draft');
  const [showPreview, setShowPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSlug, setAutoSlug] = useState(!blog?.slug);
  const [pdfUploading, setPdfUploading] = useState(false);
  
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  // Auto-generate slug from title
  useEffect(() => {
    if (autoSlug && title) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setSlug(generatedSlug);
    }
  }, [title, autoSlug]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      const imageUrl = data.publicUrl;

      // If this is for cover image
      if (e.target.id === 'cover-image-input') {
        setCoverImage(imageUrl);
      } else {
        // Insert image markdown at cursor position
        insertTextAtCursor(`\n![Image description](${imageUrl})\n`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // PDF Upload and Text Extraction
  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      alert('PDF size should be less than 20MB');
      return;
    }

    setPdfUploading(true);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = '';
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        
        let lastY = null;
        let pageText = '';
        
        for (const item of textContent.items) {
          // Check if we need a new line (significant Y position change)
          if (lastY !== null && Math.abs(item.transform[5] - lastY) > 5) {
            pageText += '\n';
          }
          pageText += item.str;
          lastY = item.transform[5];
        }
        
        fullText += pageText + '\n\n---\n\n'; // Page separator
      }

      // Basic cleanup and formatting
      let markdown = fullText
        // Remove excessive whitespace
        .replace(/[ \t]+/g, ' ')
        // Fix line breaks
        .replace(/\n{3,}/g, '\n\n')
        // Try to detect headers (lines in all caps or short bold lines)
        .replace(/^([A-Z][A-Z\s]{2,50})$/gm, '\n## $1\n')
        // Clean up page separators at end
        .replace(/\n---\n\n$/, '')
        .trim();

      // Show confirmation dialog
      const confirmReplace = window.confirm(
        `Successfully extracted ${pdf.numPages} pages from the PDF.\n\n` +
        `Do you want to:\n` +
        `• OK: Replace current content with PDF text\n` +
        `• Cancel: Append PDF text to current content\n\n` +
        `Note: Math formulas may need manual formatting with $...$ or $$...$$`
      );

      if (confirmReplace) {
        setContent(markdown);
        // Try to extract title from first line
        const firstLine = markdown.split('\n')[0].replace(/^#+\s*/, '').trim();
        if (firstLine && firstLine.length < 100 && !title) {
          setTitle(firstLine);
        }
      } else {
        setContent(prev => prev + '\n\n---\n\n## Imported from PDF\n\n' + markdown);
      }

      alert(
        `PDF imported successfully!\n\n` +
        `Tips for math content:\n` +
        `• Wrap inline math with $...$ \n` +
        `• Wrap block equations with $$...$$\n` +
        `• Use Preview to check formatting`
      );

    } catch (error) {
      console.error('Error processing PDF:', error);
      alert('Failed to process PDF. The file might be corrupted or password-protected.');
    } finally {
      setPdfUploading(false);
      // Reset input
      if (pdfInputRef.current) {
        pdfInputRef.current.value = '';
      }
    }
  };

  const insertTextAtCursor = (text) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newContent = content.substring(0, start) + text + content.substring(end);
    
    setContent(newContent);
    
    // Set cursor position after inserted text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  const handleToolbarAction = (action) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);

    const actions = {
      bold: { prefix: '**', suffix: '**', placeholder: 'bold text' },
      italic: { prefix: '_', suffix: '_', placeholder: 'italic text' },
      heading: { prefix: '\n## ', suffix: '\n', placeholder: 'Heading' },
      link: { prefix: '[', suffix: '](url)', placeholder: 'link text' },
      image: { prefix: '![', suffix: '](url)', placeholder: 'alt text' },
      code: { prefix: '`', suffix: '`', placeholder: 'code' },
      codeblock: { prefix: '\n```javascript\n', suffix: '\n```\n', placeholder: '// your code here' },
      quote: { prefix: '\n> ', suffix: '\n', placeholder: 'quote' },
      list: { prefix: '\n- ', suffix: '\n', placeholder: 'list item' },
      orderedList: { prefix: '\n1. ', suffix: '\n', placeholder: 'list item' },
      latex: { prefix: '$', suffix: '$', placeholder: 'E = mc^2' },
      latexBlock: { prefix: '\n$$\n', suffix: '\n$$\n', placeholder: '\\int_0^\\infty f(x) dx' },
      table: { 
        prefix: '\n| Header 1 | Header 2 | Header 3 |\n| --- | --- | --- |\n| ', 
        suffix: ' | Cell 2 | Cell 3 |\n', 
        placeholder: 'Cell 1' 
      },
      hr: { prefix: '\n---\n', suffix: '', placeholder: '' },
    };

    const { prefix, suffix, placeholder } = actions[action];
    const text = selectedText || placeholder;
    const newText = prefix + text + suffix;
    
    const newContent = content.substring(0, start) + newText + content.substring(end);
    setContent(newContent);

    setTimeout(() => {
      textarea.focus();
      if (!selectedText && placeholder) {
        textarea.setSelectionRange(start + prefix.length, start + prefix.length + placeholder.length);
      } else {
        textarea.setSelectionRange(start + newText.length, start + newText.length);
      }
    }, 0);
  };

  const handleSave = async (publishNow = false) => {
    if (!title.trim()) {
      alert('Please enter a title');
      return;
    }

    if (!content.trim()) {
      alert('Please enter some content');
      return;
    }

    setSaving(true);

    try {
      const blogData = {
        title: title.trim(),
        slug: slug.trim() || undefined,
        excerpt: excerpt.trim() || null,
        content: content.trim(),
        cover_image: coverImage || null,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        status: publishNow ? 'published' : status,
        ...(publishNow && !blog?.published_at ? { published_at: new Date().toISOString() } : {})
      };

      await onSave(blogData);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setSaving(false);
    }
  };

  // Markdown preview components
  const components = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={oneDark}
          language={match[1]}
          PreTag="div"
          className="rounded-xl my-4 text-sm"
          showLineNumbers
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className="bg-gray-100 text-secondary-accent px-2 py-1 rounded text-sm font-mono" {...props}>
          {children}
        </code>
      );
    },
    h1: ({ children }) => <h1 className="text-3xl font-bold mt-8 mb-4">{children}</h1>,
    h2: ({ children }) => <h2 className="text-2xl font-bold mt-6 mb-3">{children}</h2>,
    h3: ({ children }) => <h3 className="text-xl font-bold mt-4 mb-2">{children}</h3>,
    p: ({ children }) => <p className="text-grey-accent leading-relaxed mb-4">{children}</p>,
    ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-1">{children}</ol>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-secondary-accent pl-4 py-2 my-4 bg-secondary-accent/5 rounded-r">
        {children}
      </blockquote>
    ),
    a: ({ href, children }) => (
      <a href={href} className="text-secondary-accent hover:underline" target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
    img: ({ src, alt }) => (
      <figure className="my-6">
        <img src={src} alt={alt} className="w-full rounded-lg" />
        {alt && <figcaption className="text-center text-grey-accent text-sm mt-2">{alt}</figcaption>}
      </figure>
    ),
  };

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={onCancel}
                className="p-2 text-grey-accent hover:text-text-primary transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <h1 className="text-xl font-bold text-text-primary">
                {blog ? 'Edit Blog Post' : 'New Blog Post'}
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  showPreview
                    ? 'bg-secondary-accent text-white'
                    : 'bg-gray-100 text-grey-accent hover:bg-gray-200'
                }`}
              >
                {showPreview ? 'Edit' : 'Preview'}
              </button>

              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary-accent/20"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>

              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>

              <button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="px-4 py-2 bg-secondary-accent text-white rounded-lg text-sm font-medium hover:bg-opacity-90 transition-all disabled:opacity-50"
              >
                {saving ? 'Publishing...' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Editor */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm overflow-hidden"
            >
              {showPreview ? (
                /* Preview Mode */
                <div className="p-8">
                  <h1 className="text-4xl font-bold text-text-primary mb-4">{title || 'Untitled'}</h1>
                  {excerpt && <p className="text-xl text-grey-accent mb-8">{excerpt}</p>}
                  {coverImage && (
                    <img src={coverImage} alt="Cover" className="w-full rounded-xl mb-8" />
                  )}
                  <div className="prose max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkMath, remarkGfm]}
                      rehypePlugins={[rehypeKatex, rehypeRaw]}
                      components={components}
                    >
                      {content || '_No content yet..._'}
                    </ReactMarkdown>
                  </div>
                </div>
              ) : (
                /* Edit Mode */
                <div>
                  {/* Title */}
                  <div className="p-6 border-b border-gray-100">
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter your blog title..."
                      className="w-full text-3xl font-bold text-text-primary placeholder-grey-accent/50 focus:outline-none"
                    />
                  </div>

                  {/* Toolbar */}
                  <div className="px-6 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-1">
                    <button onClick={() => handleToolbarAction('bold')} className="p-2 hover:bg-gray-200 rounded" title="Bold">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12h8a4 4 0 100-8H6v8zm0 0h9a4 4 0 110 8H6v-8z" />
                      </svg>
                    </button>
                    <button onClick={() => handleToolbarAction('italic')} className="p-2 hover:bg-gray-200 rounded" title="Italic">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l-8 8" />
                      </svg>
                    </button>
                    <button onClick={() => handleToolbarAction('heading')} className="p-2 hover:bg-gray-200 rounded" title="Heading">
                      <span className="font-bold text-sm">H</span>
                    </button>
                    <div className="w-px bg-gray-300 mx-1"></div>
                    <button onClick={() => handleToolbarAction('link')} className="p-2 hover:bg-gray-200 rounded" title="Link">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </button>
                    <button 
                      onClick={() => fileInputRef.current?.click()} 
                      className="p-2 hover:bg-gray-200 rounded" 
                      title="Insert Image"
                      disabled={uploading}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <div className="w-px bg-gray-300 mx-1"></div>
                    <button onClick={() => handleToolbarAction('code')} className="p-2 hover:bg-gray-200 rounded" title="Inline Code">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                      </svg>
                    </button>
                    <button onClick={() => handleToolbarAction('codeblock')} className="p-2 hover:bg-gray-200 rounded" title="Code Block">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <div className="w-px bg-gray-300 mx-1"></div>
                    <button onClick={() => handleToolbarAction('latex')} className="p-2 hover:bg-gray-200 rounded" title="Inline LaTeX">
                      <span className="font-serif italic text-sm">∑</span>
                    </button>
                    <button onClick={() => handleToolbarAction('latexBlock')} className="p-2 hover:bg-gray-200 rounded" title="LaTeX Block">
                      <span className="font-serif italic text-sm">∫</span>
                    </button>
                    <div className="w-px bg-gray-300 mx-1"></div>
                    <button onClick={() => handleToolbarAction('quote')} className="p-2 hover:bg-gray-200 rounded" title="Quote">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </button>
                    <button onClick={() => handleToolbarAction('list')} className="p-2 hover:bg-gray-200 rounded" title="Bullet List">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    </button>
                    <button onClick={() => handleToolbarAction('table')} className="p-2 hover:bg-gray-200 rounded" title="Table">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button onClick={() => handleToolbarAction('hr')} className="p-2 hover:bg-gray-200 rounded" title="Horizontal Rule">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <div className="w-px bg-gray-300 mx-1"></div>
                    <button 
                      onClick={() => pdfInputRef.current?.click()} 
                      className={`p-2 hover:bg-gray-200 rounded flex items-center gap-1 ${pdfUploading ? 'opacity-50' : ''}`}
                      title="Import from PDF"
                      disabled={pdfUploading}
                    >
                      {pdfUploading ? (
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11v6m-3-3h6" />
                        </svg>
                      )}
                      <span className="text-xs font-medium">PDF</span>
                    </button>
                    <input
                      ref={pdfInputRef}
                      type="file"
                      accept="application/pdf"
                      onChange={handlePdfUpload}
                      className="hidden"
                    />
                  </div>

                  {/* Content Editor */}
                  <div className="p-6">
                    <textarea
                      ref={textareaRef}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Write your blog content here... 

Supports:
• **Bold** and _italic_ text
• # Headings
• [Links](url) and ![Images](url)
• `inline code` and code blocks with syntax highlighting
• LaTeX math: $E = mc^2$ or block equations
• > Blockquotes
• - Lists
• Tables

Use the toolbar above for quick formatting!"
                      className="w-full h-[500px] text-text-primary placeholder-grey-accent/50 focus:outline-none resize-none font-mono text-sm leading-relaxed"
                    />
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Cover Image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="font-semibold text-text-primary mb-4">Cover Image</h3>
              {coverImage ? (
                <div className="relative">
                  <img src={coverImage} alt="Cover" className="w-full rounded-lg" />
                  <button
                    onClick={() => setCoverImage('')}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <label className="block cursor-pointer">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-secondary-accent transition-colors">
                    <svg className="w-10 h-10 mx-auto text-grey-accent/50 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm text-grey-accent">
                      {uploading ? 'Uploading...' : 'Click to upload cover image'}
                    </p>
                  </div>
                  <input
                    id="cover-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              )}
            </motion.div>

            {/* Slug */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-text-primary">URL Slug</h3>
                <label className="flex items-center gap-2 text-sm text-grey-accent">
                  <input
                    type="checkbox"
                    checked={autoSlug}
                    onChange={(e) => setAutoSlug(e.target.checked)}
                    className="rounded"
                  />
                  Auto
                </label>
              </div>
              <input
                type="text"
                value={slug}
                onChange={(e) => {
                  setAutoSlug(false);
                  setSlug(e.target.value);
                }}
                placeholder="blog-post-url"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary-accent/20"
              />
              <p className="text-xs text-grey-accent mt-2">/blogs/{slug || 'your-slug'}</p>
            </motion.div>

            {/* Excerpt */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="font-semibold text-text-primary mb-4">Excerpt</h3>
              <textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="A brief summary of your post..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary-accent/20 resize-none"
              />
            </motion.div>

            {/* Tags */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="font-semibold text-text-primary mb-4">Tags</h3>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="React, Web Development, Tutorial"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary-accent/20"
              />
              <p className="text-xs text-grey-accent mt-2">Separate tags with commas</p>
            </motion.div>

            {/* Help */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-secondary-accent/5 rounded-xl p-6"
            >
              <h3 className="font-semibold text-text-primary mb-4">Formatting Help</h3>
              <div className="space-y-2 text-sm text-grey-accent">
                <p><code className="bg-white px-1 rounded">**bold**</code> → <strong>bold</strong></p>
                <p><code className="bg-white px-1 rounded">_italic_</code> → <em>italic</em></p>
                <p><code className="bg-white px-1 rounded">$E=mc^2$</code> → inline LaTeX</p>
                <p><code className="bg-white px-1 rounded">```js code```</code> → code block</p>
                <p><code className="bg-white px-1 rounded">[text](url)</code> → link</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;
